import { useMemo } from 'react'
import { useController, UseFormReturn } from 'react-hook-form'

import { useCombinedLogsTableFormStateStore } from '@/modules/CombinedLogs/store'
import { useGetTrips } from '@/modules/CombinedLogs/api/useGetTrips'
import { SuggestionInputTable } from '@/ui/SuggestionInputTable'
import { InputFormValues } from '@/modules/CombinedLogs/types'

interface Props {
    form: UseFormReturn<InputFormValues>
    name: 'duty' | 'originalDuty'
}

export const TableFormDutyInput = ({ form, name }: Props) => {
    const { field } = useController({ control: form.control, name })
    const { selectedTrips, selectedOriginalTrips } = useCombinedLogsTableFormStateStore()
    const { data: trips } = useGetTrips()

    const tripFieldName = name === 'duty' ? 'trip' : 'originalTrip'
    const tripValue = form.watch(tripFieldName)

    const options = useMemo(() => {
        const allTrips = trips ? [...trips.inbound, ...trips.outbound] : []
        const selected = name === 'duty' ? selectedTrips : selectedOriginalTrips

        const duties = new Set<string>()

        // Get duties from the main trip
        if (tripValue) {
            const tripObject = allTrips.find((item) => item.trip.toString() === tripValue)
            if (tripObject?.duty) {
                tripObject.duty.forEach((d) => duties.add(d.toString()))
            }
        }

        // Get duties from selected trips (return trips)
        selected.forEach((trip) => {
            if (trip.duty) {
                trip.duty.forEach((d) => duties.add(d.toString()))
            }
        })

        return Array.from(duties)
            .map((d) => ({ id: d, label: d, value: d }))
            .sort((a, b) => parseInt(a.value, 10) - parseInt(b.value, 10))
    }, [trips, tripValue, name === 'duty' ? selectedTrips : selectedOriginalTrips])

    return (
        <SuggestionInputTable
            value={field.value ?? ''}
            options={options}
            variant="table"
            type="text"
            transparent
            fullWidth
            placeholder="Duty"
            displayValueInsteadOfLabel
            onlyFromDropdown={false}
            canTabThrough
            onChange={(value) => {
                field.onChange(value)
            }}
        />
    )
}
